import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { CHAT_LABELS } from './chatLabels';
import { ChatHandoffBar } from './ChatHandoffBar';
import { ChatCallbackForm } from './ChatCallbackForm';
import { trackWidgetEvent, inferPathFromRoute, istanbulOpenNow } from '../lib/widgetAnalytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ChatBot({ language }) {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [path, setPath] = useState(null);
  const [qualified, setQualified] = useState(false);
  const [showCallback, setShowCallback] = useState(false);
  const leadFired = useRef(false);
  const [sessionId] = useState(() => 'chat_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9));
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const labels = CHAT_LABELS[language] || CHAT_LABELS.en;
  const isOpenHours = istanbulOpenNow();

  useEffect(() => {
    if (!path) setPath(inferPathFromRoute(location.pathname));
  }, [location.pathname, path]);

  useEffect(() => {
    if (isOpen && messages.length === 0) setMessages([{ role: 'bot', text: labels.greeting }]);
  }, [isOpen, messages.length, labels.greeting]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, showCallback]);
  useEffect(() => { if (isOpen) inputRef.current?.focus(); }, [isOpen]);

  const openChat = () => {
    setIsOpen(true);
    trackWidgetEvent('chat_opened', { path: path || 'unknown', page: location.pathname });
  };

  const selectPath = useCallback((p, method) => {
    setPath(p);
    trackWidgetEvent('path_selected', { path: p, method });
  }, []);

  const handleHandoff = (type) => {
    trackWidgetEvent(`handoff_${type}`, { path: path || 'unknown', qualified });
    if (type === 'callback') setShowCallback(true);
  };

  const fireLeadOnce = (handoff, p) => {
    if (leadFired.current) return;
    leadFired.current = true;
    trackWidgetEvent('lead_captured', { path: p || 'unknown', handoff });
  };

  const onCallbackSubmitted = () => fireLeadOnce('callback', path);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text }]);
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, session_id: sessionId, language, path, page_path: location.pathname })
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'bot', text: data.response || 'Sorry, please try again.' }]);
      if (!path && data.path_hint) selectPath(data.path_hint, 'inferred');
      if (data.qualifying_detail) setQualified(true);
      if (data.lead_captured) fireLeadOnce('chat', path || data.path_hint);
      (data.restaurants_suggested || []).forEach(name => trackWidgetEvent('restaurant_suggested', { restaurant: name, path: path || data.path_hint || 'unknown', page: location.pathname }));
    } catch {
      setMessages(prev => [...prev, { role: 'bot', text: 'Connection error. Please try again.' }]);
    }
    setLoading(false);
  };

  return (
    <>
      {!isOpen && (
        <button className="chatbot-toggle" onClick={openChat} data-testid="chatbot-toggle-btn" aria-label="Open chat assistant">
          <svg viewBox="0 0 24 24" fill="white" width="28" height="28"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/><path d="M7 9h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z"/></svg>
        </button>
      )}
      {isOpen && (
        <div className="chatbot-window ph-no-capture" data-testid="chatbot-window" dir={language === 'ar' ? 'rtl' : 'ltr'}>
          <div className="chatbot-header">
            <div className="chatbot-header-text">
              <span>{labels.title}</span>
              <small className={`chatbot-status ${isOpenHours ? 'is-open' : 'is-closed'}`} data-testid="chatbot-status">
                {isOpenHours ? labels.statusOpen : labels.statusClosed}
              </small>
            </div>
            <button className="chatbot-close" onClick={() => setIsOpen(false)} data-testid="chatbot-close-btn">&times;</button>
          </div>
          <div className="chatbot-messages" data-testid="chatbot-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`chatbot-msg chatbot-msg-${msg.role}`} data-testid={`chatbot-msg-${i}`}>{msg.text}</div>
            ))}
            {!path && !loading && (
              <div className="chatbot-paths" data-testid="chat-path-buttons">
                <span>{labels.pathPrompt}</span>
                <div>
                  <button type="button" onClick={() => selectPath('tours', 'button')} data-testid="chat-path-tours">{labels.pathTours}</button>
                  <button type="button" onClick={() => selectPath('investment', 'button')} data-testid="chat-path-investment">{labels.pathInvestment}</button>
                </div>
                <em>{labels.typeHint}</em>
              </div>
            )}
            {loading && <div className="chatbot-msg chatbot-msg-bot chatbot-typing">...</div>}
            {showCallback && (
              <ChatCallbackForm labels={labels} language={language} path={path} sessionId={sessionId} onSubmitted={onCallbackSubmitted} onCancel={() => setShowCallback(false)} />
            )}
            <div ref={messagesEndRef} />
          </div>
          <ChatHandoffBar labels={labels} path={path} qualified={qualified} onHandoff={handleHandoff} />
          <form className="chatbot-input-row" onSubmit={(e) => { e.preventDefault(); sendMessage(); }}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={labels.placeholder}
              className="chatbot-input ph-no-capture"
              data-testid="chatbot-input"
            />
            <button type="submit" className="chatbot-send" disabled={loading || !input.trim()} data-testid="chatbot-send-btn">
              <svg viewBox="0 0 24 24" fill="white" width="20" height="20"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
            </button>
          </form>
        </div>
      )}
    </>
  );
}
